
import PouchDB from 'pouchdb-browser';
const pouchDBMemoryAdapter = require('pouchdb-adapter-memory');

/**
 * Currently the three configs interact and have a say in how module loading works in the browser
 * 1. tsconfig.json
 * 2. rollup.config.js
 * 3. package.json in the pouchdb-adapter-memory module
 * 
 * As of this writing, The interaction causes the the shape of the object returned from the require('pouchdb-adapter-memory') call to be of the shape
 * 
 * {
 *   default: ...the actual exported function from memory-adapter...
 * }
 * 
 * This would be ok if we had a d.ts file and we were doing 
 *   import {default as X} from "pouchdb-adapter-memory";
 * 
 * However, d.ts does not exist for the version we want and hence this way to get the default out if possible.
 */
PouchDB.plugin(pouchDBMemoryAdapter.default || pouchDBMemoryAdapter);

import {ITelemetry} from "../telemetry/ITelemetry";

import {config} from "../config";
import { ServiceApi, EventSecretRefreshToken } from "../serviceApi";
import {telemetry} from "../telemetry/telemetry";

const $rootScope = {
    activeAuth: null,
    user: null
};

import {ReadThroughCache} from "./ReadThroughCache";
import {loginManager} from "../auth/loginManager";
import { EventFirebaseConnection } from "./EventFirebaseConnection";
import { Event } from "./Event";
import { EventsServiceApi } from './EventServiceApi';
import { ActiveAuthMissingError } from '../auth/activeAuth';


/**
 * $q like object
 * @param telemetry
 * @returns {any}
 * @private
 */

let $q:any = function (resolver) {
    return new Promise(resolver);
};

$q.defer = function () {
    const ret: any = {};
    const callbacks = [];
    ret.resolve = (result) => {
        callbacks.push(() => {ret.resolve(result);});
    };
    ret.reject = (err) => {
        callbacks.push(() => {ret.reject(err);})
    };
    ret.promise = new Promise((resolve, reject) => {
        ret.resolve = resolve;
        ret.reject = reject;
        callbacks.forEach((f) => {f();});
    });

    return ret;
};

$q.resolve = Promise.resolve.bind(Promise);
$q.reject = Promise.reject.bind(Promise);
$q.any = function (arrPromises) {
    var deferred = $q.defer();

    var nCalls = arrPromises.length;
    var iCalls = 0;
    var isResolved = false;

    var onCallback = function (err, result) {
        iCalls++;

        //If we got an error and its the last callback, fail with this error
        if (err && iCalls === nCalls) {
            deferred.reject(err);
            return;
        }

        //otherwise log/ignore this error
        if (err) {
            telemetry.warn("Error in one of the (m)any promises");
            telemetry.warn(err);
            return;
        }

        //If this is the first successfull call, resolve the promise
        if (!isResolved) {
            deferred.resolve(result);
            isResolved = true;
            return;
        }

        //Otherwise ignore other successes.
        return;
    };

    arrPromises.forEach(function (p) {
        p.then(onCallback.bind(null, null));
        p.catch(onCallback.bind(null));
    });

    return deferred.promise;
};

$q.all = Promise.all.bind(Promise);

var _createNameToEventIdCache = function (telemetry) {
    var keyResolver = function (eventName) {
        var publicApi = new ServiceApi(config.joyServiceConfig);
        return publicApi.getEventIdFromName(eventName)
            .then(obj => obj.eventId);
    };

    return new ReadThroughCache(telemetry, $q, keyResolver);
};

var _createEventIdToPublicInfoCache = function (telemetry) {
    var keyResolver = function (eventId) {
        var publicApi = new ServiceApi(config.joyServiceConfig);
        return publicApi.getPublicEventInfo(eventId);
    };

    return new ReadThroughCache(telemetry, $q, keyResolver);
};

var _createEventIdToEventInfoCache = function (telemetry, mgr) {
    var keyResolver = function (eventId) {

        var activeAuth = mgr.getBestActiveAuthForEventId(eventId);
        if (!activeAuth) {
            return $q.reject(new ActiveAuthMissingError("No point resolving a event without a login or a permit"));
        }

        var api = new ServiceApi(config.joyServiceConfig, activeAuth);
        return api.getEventV2(eventId);
    };
    return new ReadThroughCache(telemetry, $q, keyResolver);
};

var _createEventIdAndPasswordToActiveAuthCache = function (telemetry) {
    const encode = (eventId, password) => (JSON.stringify({eventId, password}));
    const decode = fn => (json) => fn(JSON.parse(json));
    var cache = new ReadThroughCache(telemetry, $q, decode(function ({eventId, password}) {
        var deferred = $q.defer();
        //Check the cache!
        var api = (new ServiceApi(config.joyServiceConfig, $rootScope.activeAuth))
            .getServiceApiForEventPassword(eventId, password);

        //todo: export the activeAuth out correctly.
        var activeAuth = api.getActiveAuth();

        deferred.resolve(activeAuth);

        return deferred.promise;
    }), encode);

    return cache;
};

var _createEventIdAndPasswordToPermitCache = function (telemetry, eventIdAndPasswordToActiveAuthCache) {
    const encode = (eventId, password) => (JSON.stringify({eventId, password}));
    const decode = fn => (json) => fn(JSON.parse(json));
    var cache = new ReadThroughCache(telemetry, $q, decode(function ({eventId, password}) {
        return eventIdAndPasswordToActiveAuthCache.find(eventId, password).then(function (activeAuth) {
            var defer = $q.defer();
            activeAuth.getAccessToken(function (err, accessTokenObj) {
                if (err) {
                    return defer.reject(err);
                }
                var permits = accessTokenObj.decode().payload.eventPermits;
                defer.resolve(permits[0]);
            });
            return defer.promise;
        });
    }), encode);
    return cache;
};


var _createEventSecretToActiveAuthCache = function (telemetry) {

    var cache = new ReadThroughCache(telemetry, $q, function (secret) {

        var deferred = $q.defer();

        //Check the cache!

        var api = (new ServiceApi(config.joyServiceConfig, $rootScope.activeAuth))
            .getServiceApiForEventSecret(secret);

        //todo: export the activeAuth out correctly.
        var activeAuth = api.getActiveAuth();

        deferred.resolve(activeAuth);

        return deferred.promise;
    });

    return cache;
};

var _createEventSecretToPermitCache = function (telemetry, eventSecretToActiveAuthCache) {
    var cache = new ReadThroughCache(telemetry, $q, function (secret) {

        return eventSecretToActiveAuthCache.find(secret).then(function (activeAuth) {
            var defer = $q.defer();
            activeAuth.getAccessToken(function (err, accessTokenObj) {
                if (err) {
                    return defer.reject(err);
                }

                var permits = accessTokenObj.decode().payload.eventPermits;
                defer.resolve(permits[0]);
            });
            return defer.promise;
        });
    });
    return cache;
};

var _createCacheTelemetry = function (telemetry, cacheName) {
    var t = telemetry.clone();
    t.context().cache = cacheName;
    var debug = false;

    if (debug) {
        return t;
    }

    var unitFunction = function () {};
    return {
        clone: t.clone.bind(t),
        trace: unitFunction,
        debug: unitFunction,
        info: t.info.bind(t),
        warn: t.warn.bind(t),
        error: t.error.bind(t),
    };
};

class PouchDBFailureBackupDB {

  data: Map<string, any> = new Map();

  get (key) {
    return Promise.resolve(this.data.get(key));
  }

  set (key, value) {
    this.data.set(key, value);
    return Promise.resolve(value);
  }

}

var _createPersistentKeyValueStore = function (telemetry, name) {

    var KVCache = function (telemetry, dbPromise) {
        var This = this;
        This._telemetry = telemetry;
        This._dbPromise = dbPromise;
        This._db = null;
        This._initPromise = null;
        This._init();
    };

    KVCache.prototype.setupBackupDBOnFailure = function (err) {
        if (!this._usingBackupDB) {
            this._db = new PouchDBFailureBackupDB();
            this._dbPromise = Promise.resolve(this._db);
            this._usingBackupDB = true;
            return false;
        }
        return true;
    };

    KVCache.prototype._init = function () {
        if (this._initPromise) {
            return this._initPromise;
        }

        var infoPromise = this._dbPromise
            .then((db) => {
                this._db = db;
                return this._db.info();
            })
            .catch(err => {
              this.setupBackupDBOnFailure(err);
              return {};
            });

        this._initPromise = infoPromise;
        return this._infoPromise;
    };

    KVCache.prototype._set = function (key, value) {
        var This = this;
        var defer = $q.defer();
        This._db.get(key, function (err, doc) {

            if (err && err.status !== 404) {
                return defer.reject(err);
            }

            var shouldDelete = (typeof value == "undefined" || value === null);
            var exists = (!err);

            var oldValue = doc && doc.value || null;

            if (shouldDelete) {
                if (!exists) {
                    return defer.resolve(oldValue);
                }
                else {
                    return This._db.remove(doc, function (err) {
                        if (err) {
                            return defer.reject(err);
                        }

                        return defer.resolve(oldValue);
                    });
                }
            }
            var docToUpdate = doc || {_id: key};
            docToUpdate.value = value;
            This._db.put(docToUpdate, function (err) {
                if (err) {
                    return defer.reject(err);
                }
                return defer.resolve(oldValue);
            });
        });
        return defer.promise;
    };
    KVCache.prototype.set = function (key, value) {
        var This = this;
        var setPromise = This._init().then(This._set.bind(This, key, value));
        setPromise.then(function (oldValue) {
            This._telemetry.debug(key + ": " + oldValue + " >> " + value);
        })
            .catch(function (err) {
                This._telemetry.error("Error setting key: " + key);
                This._telemetry.error(err);
                if (!This.setupBackupDBOnFailure(err)) return This.set(key, value);
            });
        return setPromise;
    };

    KVCache.prototype.get = function (key) {
        var This = this;
        var defer = $q.defer();

        This._init().then(function () {
            This._db.get(key, function (err, doc) {
                if (err) {
                    if (err.message !== 'missing' && !This.setupBackupDBOnFailure(err)) return defer.resolve(This.get(key));
                    return defer.resolve(null);
                }
                return defer.resolve(doc.value);
            });
            return;
        });

        return defer.promise;
    };
    const lsTest = () => {
        // https://stackoverflow.com/questions/16427636/check-if-localstorage-is-available
        var test = 'test';
        try {
            localStorage.setItem(test, test);
            localStorage.removeItem(test);
            return true;
        } catch(e) {
            return false;
        }
    }

    const tryMemoryAdapter = () => {
        try {
            return new PouchDB(name, {adapter: 'memory'});
        }
        catch (err) {
            return Promise.reject(err);
        }
    }

    const dbPromise = (() => {
        if (!lsTest()) {
            return tryMemoryAdapter();
        }
        // try using pouchdb
        //...if it fails, try memory adapter
        return Promise.resolve()
          .then(() => new PouchDB(name))
          .then(db => {
            return db.info()
              .then(() => new PouchDB(name))
              .catch(err => tryMemoryAdapter())
        }).catch(err => {
            return tryMemoryAdapter();
        });
    })();
    return new KVCache(telemetry, dbPromise);
};

/**
 * EventsManager - Manages a set of events, and caches related to these events
 * @constructor
 */

export class EventsManager {

    private telemetry: ITelemetry;
    private eventNames;
    public eventPublicInfoCache;
    public eventInfoCache;
    public eventIdAndPasswordToActiveAuthCache;
    public eventIdAndPasswordToPermitCache;
    public eventSecretToActiveAuthCache;
    public eventSecretToPermitCache;
    public eventIdToEventPasswordKVStore;
    public eventIdToEventSecretKVStore;
    public eventIdToCurrentPersonIdKVStore;
    private events: {[key: string]: Event} = {};
    private activeAuths: {[key:string]: any} = {};
    private eventConnection: EventFirebaseConnection;
    private lastActiveAuth = null;

    constructor(telemetry: ITelemetry) {
        this.telemetry = telemetry;
        const ct = _createCacheTelemetry.bind(null, this.telemetry);

        this.eventNames = _createNameToEventIdCache(ct("eventNamesToEventId"));
        this.eventPublicInfoCache = _createEventIdToPublicInfoCache(ct("eventIdToPublicInfo"));
        this.eventInfoCache = _createEventIdToEventInfoCache(ct("eventIdToEventInfo"), this);
        this.eventIdAndPasswordToActiveAuthCache = _createEventIdAndPasswordToActiveAuthCache(ct("eventPasswordToActiveAuth"))
        this.eventSecretToActiveAuthCache = _createEventSecretToActiveAuthCache(ct("eventSecretToActiveAuth"));

        this.eventIdAndPasswordToPermitCache = _createEventIdAndPasswordToPermitCache(
            ct("eventPasswordToPermitCode"),
            this.eventIdAndPasswordToActiveAuthCache
        );
        this.eventSecretToPermitCache = _createEventSecretToPermitCache(
            ct("eventSecretToPermitCode"),
            this.eventSecretToActiveAuthCache
        );
        this.eventIdToEventPasswordKVStore = _createPersistentKeyValueStore(
            ct("eventIdToEventPasswordMap"),
            "eventIdToEventPasswordMap"
        );
        this.eventIdToEventSecretKVStore = _createPersistentKeyValueStore(
            ct("eventIdToEventSecretMap"),
            "eventIdToEventSecretMap"
        );
        this.eventIdToCurrentPersonIdKVStore = _createPersistentKeyValueStore(
            ct("eventIdToCurrentPersonIdMap"),
            "eventIdToCurrentPersonIdMap"
        );

        this.eventConnection = new EventFirebaseConnection(this);
        loginManager.activeAuthObservable().subscribe((activeAuth) => {
            this.onActiveAuthChanged(activeAuth, this.lastActiveAuth);
            this.lastActiveAuth = activeAuth;
        });

        /**
         * TODO: Corrigan/Kaiwalya
         */

        /*
        //Kick off the silent login process
        loginService.ensureAuth({
            isRequired: false,
            showPublicLoginView: false,
            promptSignUp: false
        });
        */
    }

    public getPromiseLib() {
        return $q;
    };

    public getTelemetry() {
        return this.telemetry;
    };

    public getEventConnection() {
        return this.eventConnection;
    }

    public setCurrentPersonIdForEventId(eventId: string, personId: string) {
        return this.eventIdToCurrentPersonIdKVStore.set(eventId, personId);
    };

    public getCurrentPersonIdForEventId(eventId: string) {
        return this.eventIdToCurrentPersonIdKVStore.get(eventId);
    };

    /**
     * Converts a active auth to a string id, so you can compare them with each other instead of
     * trying to see in their instances are the same
     */
    public getActiveAuthKey(activeAuth) {
        return (activeAuth && activeAuth.refreshToken() && activeAuth.refreshToken().token() || null);
    };

    public getActiveAuth() {
        const activeAuth = $rootScope.activeAuth;
        return (this.getActiveAuthKey(activeAuth) && activeAuth || null);
    };

    public getUserId() {
        return $rootScope.user && $rootScope.user.id;
    };

    public getBestActiveAuthForEventId(eventId: string) {
        return this.getActiveAuth() || this.getEventSecretActiveAuthForEventId(eventId);
    };

    public getBestServiceApiForEvent(event: Event) {
        const activeAuth = this.getBestActiveAuthForEventId(event.getEventId());
        return new EventsServiceApi(event, config.joyServiceConfig, activeAuth);
    };

    public getEventSecretActiveAuthForEventId(eventId: string) {
        if (!eventId) {
            return null;
        }
        return this.activeAuths[eventId];
    };

    public getEventInfoCache() {
        return this.eventInfoCache;
    };

    public getEventPublicInfoCache() {
        return this.eventPublicInfoCache;
    };

    public getEventNameCache() {
        return this.eventNames;
    };

    private onActiveAuthChanged_shouldIgnore(oldActiveAuth, newActiveAuth) {
        /**
         * Currently when we go to moments we push the active auth as the currently logged in user
         * if we are getting an event because of this, then we can ignore it.
         */
        const newActiveAuthKey = this.getActiveAuthKey(newActiveAuth);
        const oldActiveAuthKey = this.getActiveAuthKey(oldActiveAuth);

        //empty auth to event secret auth?
        const switchingToEventSecretAuth = !oldActiveAuthKey &&
            newActiveAuthKey &&
            newActiveAuth.refreshToken() instanceof EventSecretRefreshToken;

        //event secret auth to empty auth?
        const switchingFromEventSecretAuth = !newActiveAuthKey &&
            oldActiveAuthKey &&
            oldActiveAuth.refreshToken() instanceof EventSecretRefreshToken;

        if (switchingToEventSecretAuth || switchingFromEventSecretAuth) {
            return true;
        }

        return false;
    };

    private onActiveAuthChanged(newActiveAuth, oldActiveAuth) {
        //ignore change if its to/from the event secret auth
        if (this.onActiveAuthChanged_shouldIgnore(newActiveAuth, oldActiveAuth)) {
            this.getTelemetry().info("Auth Change Ignored");
            return;
        }

        this.eventInfoCache.cacheInvalidateAllKeys();
        this.eventConnection.onActiveAuthChanged(newActiveAuth);
        $rootScope.activeAuth = newActiveAuth;
    };

    private checkForSecretOrPassword = function(eventId){
        const This = this;
        const getBySecretForKVStore = (kvStore) => ([eventSecret, eventId]) =>
        {
            const args = eventId ? [eventSecret, eventId] : [eventSecret]
            return this.getEventBySecret.apply(this, args)
                .catch((err) => {
                    const telemetry = This.getTelemetry();
                    telemetry.error(err);
                    if (err && err.statusCode && err.statusCode === 404) {
                        telemetry.warn({
                            msg: "Removing invalid event password/secret",
                            eventSecret: eventSecret
                        });
                        kvStore.set(eventId, null)
                            .then(() => {
                                telemetry.info("Stale password/secret removed");
                            })
                            .catch(() => {
                                telemetry.error("Stale password/secret not removed");
                                telemetry.error(err);
                            });
                    }
                    return This.events[eventId];
                });
        }
        return Promise.all([
          this.eventIdToEventSecretKVStore.get(eventId),
          this.eventIdToEventPasswordKVStore.get(eventId)
        ]).then(([secret, password]) => {
            if(secret){
                return getBySecretForKVStore(this.eventIdToEventSecretKVStore)([secret, null]);
            }else if (password){
                return getBySecretForKVStore(this.eventIdToEventPasswordKVStore)([password, eventId]);
            }else{
                return This.events[eventId];
            }
        });
      }

    /**
     * Get a promise for a event with the given namegetEventBySecret
     * @param eventName
     * @returns {*}
     */
    public getEventByName(eventName: string): Promise<Event> {
        return this.eventNames.find(eventName)
            .then((eventId: string) => {
                return this.getEventById(eventId);
            });
    };

    /**
     * Get a promise for a event with the given eventId
     * @param eventId
     * @returns {*}
     */
    public getEventById(eventId: string): Promise<Event> {
        return $q.resolve().then(() => {
            if (eventId in this.events) {
                return this.events[eventId];
            }

            this.events[eventId] = new Event(this, eventId);
            this.events[eventId].getTelemetry().debug("tracking started");
            //After getting the event with an id,
            //try to check if we have a stored secret for the event
            return this.checkForSecretOrPassword(eventId);
        });
    };

    private eventBySecretOrPassword(permitCache, activeAuthCache, kvStore){
        return function(eventId, eventSecret){
            var This = this;
            var deferred = $q.defer();
            const args = eventId ? [eventId, eventSecret] : [eventSecret];
            permitCache.find.apply(permitCache, args).then(function (permit) {
                activeAuthCache.find.apply(activeAuthCache, args).then(function (activeAuth) {
                    var eventId = permit.eventId;
                    //Save the code for future
                    kvStore.set(eventId, eventSecret);
                    This.activeAuths[eventId] = activeAuth;

                    /*
                    If we already have a event with this id, && it does not have a eventInfo
                    we need to inform it about a new way to get it!
                    */

                    if (eventId in This.events && !This.eventInfoCache.get(eventId)) {
                        This.events[eventId].onActiveAuthChanged();
                        //We just inform event connection that active auth has changed, we dont provide a event
                        //Event connection is smart enough to figure is its for the "connected" event
                        This.eventConnection.onActiveAuthChanged();
                    }

                    deferred.resolve(This.getEventById(eventId));
                }, function(err) {
                    return deferred.reject(err);
                });
            }, function(err) {
                return deferred.reject(err);
            });
            return deferred.promise;
        }
    }


    private getEventBySecret_permit = function (eventSecret) {
        return this.eventBySecretOrPassword(
          this.eventSecretToPermitCache,
          this.eventSecretToActiveAuthCache,
          this.eventIdToEventSecretKVStore
        ).apply(this, [null, eventSecret]);
    };

    private getEventByPassword_permit = function (eventId, eventPassword) {
        return this.eventBySecretOrPassword(
          this.eventIdAndPasswordToPermitCache,
          this.eventIdAndPasswordToActiveAuthCache,
          this.eventIdToEventPasswordKVStore
        ).apply(this, [eventId, eventPassword]);
    };


    private reloadActiveAuth(eventId){
        /*
            If we already have a event with this id, && it does not have a eventInfo
            we need to inform it about a new way to get it!
        */
        if (eventId in this.events) {
            /*We want to pass the optional forceReload parameter here since the user
            auth has not changed but we do we to force reload the event*/
            this.events[eventId].onActiveAuthChanged(true);
            //We just inform event connection that active auth has changed, we dont provide a event
            //Event connection is smart enough to figure is its for the "connected" event
            this.eventConnection.onActiveAuthChanged();
        }
    }

    private getEventByPassword_join = function (eventId, eventPassword){
        const api = new ServiceApi(config.joyServiceConfig, this.getActiveAuth());
        return api.queryGraphQLWithErrors(`
            mutation{
                event(eventId: "${eventId}") {
                    joinWithSecret(secret: "${eventPassword}"){
                        eventAcl_role
                    }
                }
            }
          `)
            .then(() => {
                this.eventIdToEventPasswordKVStore.set(eventId, null);
                this.reloadActiveAuth(eventId);
                return this.getEventById(eventId);
            }).catch((err) => {
                this.telemetry.error("error with sign-in ", err.message);
                throw err;
            });
      }

    private getEventBySecret_join(eventSecret) {
        const api = new ServiceApi(config.joyServiceConfig, this.getActiveAuth());
        return api.joinEventWithSecret(eventSecret)
            .then(eventObj => {
                const eventId = eventObj.eventId;
                //Remove the key from the store, if you have joined the event after login
                this.eventIdToEventSecretKVStore.set(eventId, null);
                this.reloadActiveAuth(eventId)
                return this.getEventById(eventId);
            });
    };

    public getEventBySecret(eventSecret, eventId?) {
        let join;
        const activeAuth = this.getActiveAuth();
        if(eventId){
            join = this.getEventByPassword(eventId, eventSecret);
        }else{
            join = this.getEventByCode(eventSecret);
        }
        return join;
    };

    public getEventByCode(eventSecret){
        let join;
        if(this.getActiveAuth()){
            join = this.getEventBySecret_join(eventSecret);
        }else{
            join = this.getEventBySecret_permit(eventSecret);
        }
        return join;
    }

    public getEventByPassword(eventId, eventPassword){
        let join;
        if(this.getActiveAuth()){
            join = this.getEventByPassword_join(eventId, eventPassword);
        }else{
            join = this.getEventByPassword_permit(eventId, eventPassword);
        }
        return join;
    }
    /**
     * Most useful when logging out to complete the rsvp as someone else.
     *
     * @param eventId
     * @param eventSecret
     * @param callback
     * @returns {*}
     * @public
    */
    public saveEventSecretForEventIdAndLogout(eventId: string, eventSecret: string) {
        return this.eventIdToEventSecretKVStore.set(eventId, eventSecret).then(() => {
            return this.logout(eventId);
        }).catch(()=>{
            return this.logout(eventId);
        })
    };

    /**
     * Public for reasons relating to symmetry of the two versions of
     * EventsManager and Event (js and ts)
     * Should not be called from the outside, public because it needs to be
     * called from Event
     * @returns {PromiseLike<any>}
     * @public
     */
    public logout(eventId?: string) : Promise<any>{
        return loginManager.logout({
            eventId, // logout from this Event
        });
    }

    private saveEVentPassword(eventId, eventPassword){
        return this.eventIdToEventPasswordKVStore.set(eventId, eventPassword);
    }
    private saveEventSecret(eventId, eventSecret){
        return this.eventIdToEventSecretKVStore.set(eventId, eventSecret);
    }
}

export const eventsManager = new EventsManager(telemetry);
